import { ToastType } from '@components/toast/ToastType.enum';
import { QuoteBindingStatus } from '@lio/rule-engine';
import { TQuoteApiResponse } from '@models/quotes';
import { getMultiPolicyQuoteIds, formatCurrentQuoteBindingInformation } from '@services/quotes/helpers';
import { useQuoteQuoteBindings } from '@services/quotes/quoteBinding/useQuoteQuoteBindings';
import { useSearchQuoteBindingsForQuotes } from '@services/quotes/quoteBinding/useSearchQuoteBindingsForQuotes';
import { useToastStore } from '@store/Toast';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export const useCheckQuoteActiveBinding = (
  quote: TQuoteApiResponse,
  {
    redirectToInProgress,
    redirectToDetails,
    pollActive = false,
  }: {
    redirectToInProgress?: boolean;
    redirectToDetails?: true;
    pollActive?: boolean;
  } = {},
) => {
  const navigate = useNavigate();
  const { enablePersistToast } = useToastStore();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [awaitingQuotesBeingBound, setAwaitingQuotesBeingBound] = useState(false);
  const quoteIds: string[] = useMemo(() => getMultiPolicyQuoteIds(quote), [quote]);
  const { quoteBindings, isLoading } = useSearchQuoteBindingsForQuotes(quoteIds);
  const quoteId = quote?.id;

  const quotesAreBinding = quoteBindings?.some(
    (lb) => lb?.status === QuoteBindingStatus.Pending || lb?.status === QuoteBindingStatus.InProgress,
  );

  const { data: latestPolledQuoteBindings, isFetching } = useQuoteQuoteBindings({
    quoteBindings,
    enabled: quotesAreBinding && pollActive,
  });

  const { currentQuoteLatestBindingStatus, latestPolledOrSearchedIsActive, latestPolledOrSearchedCompleted } = useMemo(
    () => formatCurrentQuoteBindingInformation({ currentQuoteId: quoteId, latestPolledQuoteBindings, quoteBindings }),
    [latestPolledQuoteBindings, quoteBindings, quoteId],
  );

  if (!awaitingQuotesBeingBound && quotesAreBinding && !latestPolledOrSearchedCompleted) {
    setAwaitingQuotesBeingBound(true);
  }

  useEffect(() => {
    if (latestPolledOrSearchedIsActive && redirectToInProgress) {
      navigate(`/quote/${quoteId}/bind-in-progress`, {
        replace: false,
        state: true,
      });
    }

    if (isLoading || isFetching) {
      return;
    }

    if (latestPolledOrSearchedCompleted) {
      if (awaitingQuotesBeingBound) {
        const newStatus = currentQuoteLatestBindingStatus;

        setAwaitingQuotesBeingBound(false);

        const handleCleanup = () => {
          quoteIds?.forEach((quoteId) => {
            queryClient.invalidateQueries({
              queryKey: ['quote', { quoteId }],
            });
          });
          enablePersistToast();
        };

        if (newStatus === QuoteBindingStatus.Failed) {
          handleCleanup();
          enqueueSnackbar('Quote binding has failed', {
            persist: true,
            variant: ToastType.Error,
          });
        } else if (newStatus === QuoteBindingStatus.Succeeded) {
          handleCleanup();
          enqueueSnackbar('Quote bound successfully', {
            variant: ToastType.Success,
          });
        }
      }

      if (redirectToDetails) {
        navigate(`/quote/${quoteId}`, {
          replace: true,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    navigate,
    quoteId,
    redirectToDetails,
    redirectToInProgress,
    isLoading,
    isFetching,
    quoteIds,
    latestPolledOrSearchedCompleted,
    latestPolledOrSearchedIsActive,
    currentQuoteLatestBindingStatus,
    awaitingQuotesBeingBound,
    queryClient,
    enqueueSnackbar,
  ]);

  return {
    isFetching,
    isLoading,
    quoteBindings,
  };
};
